import React from 'react';

interface PanelToggleButtonProps {
  onClick: () => void;
  mode: 'show' | 'hide';
}

// Position the toggle tab flush against the right edge of the left panel
// at every breakpoint:
//   - 'show' mode (panel collapsed to a 48 px slim rail) → left-12 (48 px)
//   - 'hide' mode (panel expanded) → matches the panel's responsive
//     widths from DetailLeftPanel.tsx (`w-[420px] lg:w-[460px]`).
//
// Previously the component took a `leftOffset` prop that callers hard-
// coded to 460 px regardless of viewport, leaving a visible 40 px gap
// between the tab and the panel edge between md (768 px) and lg
// (1024 px) — the panel was 420 px there but the tab sat at 460 px.
const POSITION_CLASS: Record<'show' | 'hide', string> = {
  show: 'left-12',
  hide: 'md:left-[420px] lg:left-[460px]',
};

export const PanelToggleButton: React.FC<PanelToggleButtonProps> = ({
  onClick,
  mode,
}) => {
  const isShow = mode === 'show';

  return (
    <button
      onClick={onClick}
      className={`fixed top-4 ${POSITION_CLASS[mode]} bg-figma-lime/80 hover:bg-figma-lime transition-colors rounded-r-lg shadow-xl z-[900] hidden md:flex flex-col items-center justify-center gap-1.5 border-l border-figma-lime/30 py-2 px-2`}
      aria-label={`${isShow ? 'Show' : 'Hide'} panel`}
      title={`${isShow ? 'Show' : 'Hide'} panel`}
    >
      <span className="text-figma-panel text-sm font-roboto font-semibold [writing-mode:vertical-lr] tracking-wide">
        {isShow ? 'Show' : 'Hide'}
      </span>
      <svg
        className="w-4 h-4 text-figma-panel"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        {isShow ? (
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
        ) : (
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
        )}
      </svg>
    </button>
  );
};
